/**
 * Störung-Detail - Legacy-Look
 * PR-8b: Minimal renderbar mit Legacy-Struktur
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { ModuleBadge } from '../components/ModuleBadge';
import {
  AlertTriangle,
  ArrowLeft,
  RefreshCw,
  Clock,
  WashingMachine,
  User,
  Wrench,
  CheckCircle,
} from 'lucide-react';
import { formatBerlinDateTimeDE, formatBerlinDateLongDE } from '../utils/berlinDate';

interface Disruption {
  id: string;
  shiftDate: string;
  shiftType: string;
  startedAt: string;
  endedAt: string | null;
  categoryId: string | null;
  categoryCodeSnapshot: string | null;
  categoryNameSnapshot: string | null;
  category: { id: string; code: string; name: string; color: string | null } | null;
  description: string | null;
  techStatus: string;
  rootCause: string | null;
  repairAction: string | null;
  partsUsed: string | null;
  workMinutes: number | null;
  techNotes: string | null;
  machine: { id: string; name: string } | null;
  station: { id: string; name: string } | null;
  recordedBy: { displayName: string };
  technician: { displayName: string } | null;
}

// Helper: Kategorie-Name aus Snapshot oder Relation
const getCategoryName = (d: Disruption): string => {
  return d.categoryNameSnapshot ?? d.category?.name ?? '–';
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  OPEN: { label: 'Offen', color: 'bg-red-100 text-red-800' },
  IN_PROGRESS: { label: 'In Bearbeitung', color: 'bg-yellow-100 text-yellow-800' },
  WAITING_PARTS: { label: 'Warte auf Teile', color: 'bg-orange-100 text-orange-800' },
  RESOLVED_CONFIRMED: { label: 'Bestätigt', color: 'bg-green-100 text-green-800' },
  REOPENED: { label: 'Wieder geöffnet', color: 'bg-red-100 text-red-800' },
};

const ROOT_CAUSE_LABELS: Record<string, string> = {
  WEAR: 'Verschleiß',
  DEFECT: 'Defekt',
  CONTAMINATION: 'Verschmutzung',
  OPERATOR_ERROR: 'Bedienfehler',
  POWER_ISSUE: 'Strom/Elektrik',
  SOFTWARE: 'Software',
  UNKNOWN: 'Unbekannt',
  OTHER: 'Sonstiges',
};

const SHIFT_LABELS: Record<string, string> = {
  EARLY: 'Frühschicht',
  LATE: 'Spätschicht',
  SATURDAY: 'Samstagsschicht',
};

export default function DisruptionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [disruption, setDisruption] = useState<Disruption | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    // Nutze korrekten API-Endpoint
    fetch(`/api/disruptions/${id}`, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error('Störung nicht gefunden');
        return res.json();
      })
      .then(setDisruption)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  // Nutze zentrale Berlin-Formatter aus berlinDate.ts
  const formatDateTime = formatBerlinDateTimeDE;
  const formatDate = formatBerlinDateLongDE;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Link
              to="/admin/disruptions"
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Übersicht
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
              Störung
              <ModuleBadge module="disruptions" />
            </h1>
            {disruption && (
              <p className="text-sm text-slate-500 mt-1">
                {formatDate(disruption.shiftDate)} – {SHIFT_LABELS[disruption.shiftType] || disruption.shiftType}
              </p>
            )}
          </div>
          <button
            onClick={() => window.location.reload()}
            className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg border p-8 text-center text-slate-500">Laden...</div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center text-red-600">{error}</div>
        ) : disruption ? (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Info Card */}
            <div className="bg-white rounded-lg border p-6 space-y-4">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-400" />
                Störungsdaten
              </h2>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Status</span>
                  <span className={`px-2 py-1 text-xs rounded ${STATUS_CONFIG[disruption.techStatus]?.color || 'bg-slate-100'}`}>
                    {STATUS_CONFIG[disruption.techStatus]?.label || disruption.techStatus}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-500">Kategorie</span>
                  <span>{getCategoryName(disruption)}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-500">Beginn</span>
                  <span>{formatDateTime(disruption.startedAt)}</span>
                </div>
                
                {disruption.endedAt && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Ende</span>
                    <span>{formatDateTime(disruption.endedAt)}</span>
                  </div>
                )}
                
                <div className="pt-3 border-t">
                  <span className="text-slate-500 text-sm">Beschreibung</span>
                  <p className="mt-1">{disruption.description || '–'}</p>
                </div>
              </div>
            </div>

            {/* Maschine/Station Card */}
            <div className="bg-white rounded-lg border p-6 space-y-4">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <WashingMachine className="w-5 h-5 text-slate-400" />
                Betroffene Einheit
              </h2>
              
              <div className="space-y-3">
                {disruption.machine && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Maschine</span>
                    <span className="font-medium">{disruption.machine.name}</span>
                  </div>
                )}
                
                {disruption.station && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Station</span>
                    <span>{disruption.station.name}</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-slate-500">Gemeldet von</span>
                  <span>{disruption.recordedBy.displayName}</span>
                </div>
              </div>
            </div>

            {/* Techniker Card */}
            <div className="bg-white rounded-lg border p-6 space-y-4">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Wrench className="w-5 h-5 text-slate-400" />
                Techniker-Bereich
              </h2>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Techniker</span>
                  <span>{disruption.technician?.displayName || '–'}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-slate-500">Ursache</span>
                  <span>{ROOT_CAUSE_LABELS[disruption.rootCause || ''] || disruption.rootCause || '–'}</span>
                </div>
                
                {disruption.workMinutes && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Arbeitszeit</span>
                    <span>{disruption.workMinutes} min</span>
                  </div>
                )}
                
                {disruption.repairAction && (
                  <div className="pt-3 border-t">
                    <span className="text-slate-500 text-sm">Maßnahme</span>
                    <p className="mt-1">{disruption.repairAction}</p>
                  </div>
                )}
                
                {disruption.partsUsed && (
                  <div className="pt-3 border-t">
                    <span className="text-slate-500 text-sm">Verwendete Teile</span>
                    <p className="mt-1">{disruption.partsUsed}</p>
                  </div>
                )}
                
                {disruption.techNotes && (
                  <div className="pt-3 border-t">
                    <span className="text-slate-500 text-sm">Notizen</span>
                    <p className="mt-1">{disruption.techNotes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Resolved Info */}
            {disruption.techStatus === 'RESOLVED_CONFIRMED' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center gap-2 text-green-800">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">Störung behoben</span>
                </div>
                {disruption.endedAt && (
                  <p className="text-sm text-green-700 mt-2">
                    Behoben am {formatDateTime(disruption.endedAt)}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </AdminLayout>
  );
}
